import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Cookie, Shield, Settings, Check, ExternalLink } from 'lucide-react';

// Complete cookie consent translations for all 5 languages
const COOKIE_TRANSLATIONS = {
  en: {
    title: 'Cookie Consent',
    description: 'We use cookies to enhance your experience, analyze traffic, and serve personalized ads. By clicking "Accept All", you consent to our use of cookies.',
    privacyLink: 'Privacy Policy',
    cookiePolicyLink: 'Cookie Policy',
    necessary: 'Strictly Necessary',
    necessaryDesc: 'Essential for the website to function properly. These cookies cannot be disabled.',
    analytics: 'Performance & Analytics',
    analyticsDesc: 'Help us understand how visitors interact with our website by collecting anonymous information.',
    advertising: 'Targeting & Advertising',
    advertisingDesc: 'Used to deliver relevant advertisements and track ad campaign performance.',
    acceptAll: 'Accept All',
    acceptNecessary: 'Reject All',
    customize: 'Customize',
    savePreferences: 'Save My Preferences',
    close: 'Close',
    manageSettings: 'Cookie Settings',
    consentRecorded: 'Your preferences have been saved',
    learnMore: 'Learn more in our'
  },
  de: {
    title: 'Cookie-Einwilligung',
    description: 'Wir verwenden Cookies, um Ihre Erfahrung zu verbessern, den Datenverkehr zu analysieren und personalisierte Werbung anzuzeigen. Mit Klick auf "Alle akzeptieren" stimmen Sie der Verwendung von Cookies zu.',
    privacyLink: 'Datenschutzerklärung',
    cookiePolicyLink: 'Cookie-Richtlinie',
    necessary: 'Unbedingt erforderlich',
    necessaryDesc: 'Für das ordnungsgemäße Funktionieren der Website unerlässlich. Diese Cookies können nicht deaktiviert werden.',
    analytics: 'Leistung & Analyse',
    analyticsDesc: 'Helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem anonyme Informationen gesammelt werden.',
    advertising: 'Targeting & Werbung',
    advertisingDesc: 'Werden verwendet, um relevante Werbung zu liefern und die Leistung von Werbekampagnen zu verfolgen.',
    acceptAll: 'Alle akzeptieren',
    acceptNecessary: 'Alle ablehnen',
    customize: 'Anpassen',
    savePreferences: 'Einstellungen speichern',
    close: 'Schließen',
    manageSettings: 'Cookie-Einstellungen',
    consentRecorded: 'Ihre Einstellungen wurden gespeichert',
    learnMore: 'Mehr erfahren in unserer'
  },
  fr: {
    title: 'Consentement aux Cookies',
    description: 'Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic et diffuser des publicités personnalisées. En cliquant sur "Tout accepter", vous consentez à notre utilisation des cookies.',
    privacyLink: 'Politique de Confidentialité',
    cookiePolicyLink: 'Politique des Cookies',
    necessary: 'Strictement nécessaires',
    necessaryDesc: 'Essentiels au bon fonctionnement du site. Ces cookies ne peuvent pas être désactivés.',
    analytics: 'Performance & Analyse',
    analyticsDesc: 'Nous aident à comprendre comment les visiteurs interagissent avec notre site en collectant des informations anonymes.',
    advertising: 'Ciblage & Publicité',
    advertisingDesc: 'Utilisés pour diffuser des publicités pertinentes et suivre les performances des campagnes publicitaires.',
    acceptAll: 'Tout accepter',
    acceptNecessary: 'Tout refuser',
    customize: 'Personnaliser',
    savePreferences: 'Enregistrer mes préférences',
    close: 'Fermer',
    manageSettings: 'Paramètres des Cookies',
    consentRecorded: 'Vos préférences ont été enregistrées',
    learnMore: 'En savoir plus dans notre'
  },
  it: {
    title: 'Consenso ai Cookie',
    description: 'Utilizziamo i cookie per migliorare la tua esperienza, analizzare il traffico e mostrare annunci personalizzati. Cliccando su "Accetta tutto", acconsenti all\'uso dei cookie.',
    privacyLink: 'Informativa Privacy',
    cookiePolicyLink: 'Cookie Policy',
    necessary: 'Strettamente necessari',
    necessaryDesc: 'Essenziali per il corretto funzionamento del sito. Questi cookie non possono essere disattivati.',
    analytics: 'Prestazioni e Analisi',
    analyticsDesc: 'Ci aiutano a capire come i visitatori interagiscono con il nostro sito raccogliendo informazioni anonime.',
    advertising: 'Targeting e Pubblicità',
    advertisingDesc: 'Utilizzati per fornire pubblicità pertinenti e monitorare le prestazioni delle campagne pubblicitarie.',
    acceptAll: 'Accetta tutto',
    acceptNecessary: 'Rifiuta tutto',
    customize: 'Personalizza',
    savePreferences: 'Salva le mie preferenze',
    close: 'Chiudi',
    manageSettings: 'Impostazioni Cookie',
    consentRecorded: 'Le tue preferenze sono state salvate',
    learnMore: 'Scopri di più nella nostra'
  },
  es: {
    title: 'Consentimiento de Cookies',
    description: 'Usamos cookies para mejorar tu experiencia, analizar el tráfico y mostrar anuncios personalizados. Al hacer clic en "Aceptar todo", consientes el uso de cookies.',
    privacyLink: 'Política de Privacidad',
    cookiePolicyLink: 'Política de Cookies',
    necessary: 'Estrictamente necesarias',
    necessaryDesc: 'Esenciales para el correcto funcionamiento del sitio. Estas cookies no se pueden desactivar.',
    analytics: 'Rendimiento y Análisis',
    analyticsDesc: 'Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio recopilando información anónima.',
    advertising: 'Segmentación y Publicidad',
    advertisingDesc: 'Se utilizan para ofrecer anuncios relevantes y realizar un seguimiento del rendimiento de las campañas publicitarias.',
    acceptAll: 'Aceptar todo',
    acceptNecessary: 'Rechazar todo',
    customize: 'Personalizar',
    savePreferences: 'Guardar mis preferencias',
    close: 'Cerrar',
    manageSettings: 'Configuración de Cookies',
    consentRecorded: 'Tus preferencias han sido guardadas',
    learnMore: 'Más información en nuestra'
  }
};

/**
 * CookieBanner - GDPR/CCPA/ePrivacy Compliant Native Cookie Consent
 * 
 * Features:
 * - Multi-language support (5 languages)
 * - Granular consent options (Necessary, Analytics, Advertising)
 * - Google AdSense & Analytics compliant
 * - Google Consent Mode v2 support
 * - Accessible (WCAG 2.1 AA)
 * - Persistent floating button after consent
 * - No external dependencies
 */
const CookieBanner = () => {
  const { language } = useLanguage();
  
  // Initialize state from localStorage to avoid setState in effect
  const [showBanner, setShowBanner] = useState(() => {
    const consent = localStorage.getItem('cookie_consent');
    const consentTimestamp = localStorage.getItem('cookie_consent_timestamp');
    
    if (!consent) return true; // No consent yet - show banner
    if (consentTimestamp) {
      const oneYearAgo = Date.now() - (365 * 24 * 60 * 60 * 1000);
      if (parseInt(consentTimestamp) < oneYearAgo) return true; // Expired
    }
    return false; // Valid consent exists
  });
  
  const [showDetails, setShowDetails] = useState(false);
  
  const [hasConsented, setHasConsented] = useState(() => {
    const consent = localStorage.getItem('cookie_consent');
    const consentTimestamp = localStorage.getItem('cookie_consent_timestamp');
    
    if (!consent) return false;
    if (consentTimestamp) {
      const oneYearAgo = Date.now() - (365 * 24 * 60 * 60 * 1000);
      if (parseInt(consentTimestamp) < oneYearAgo) return false;
    }
    return true;
  });
  
  const [preferences, setPreferences] = useState(() => {
    const savedPrefs = localStorage.getItem('cookie_preferences');
    if (savedPrefs) {
      try {
        return JSON.parse(savedPrefs);
      } catch {
        // Invalid JSON, return defaults
      }
    }
    return {
      necessary: true,
      analytics: false,
      advertising: false
    };
  });

  const t = COOKIE_TRANSLATIONS[language] || COOKIE_TRANSLATIONS.en;
  const langPrefix = language === 'en' ? '' : `/${language}`;

  // Initialize Google Consent Mode v2 with default denied
  const initConsentMode = useCallback(() => {
    if (window.gtag) {
      window.gtag('consent', 'default', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'functionality_storage': 'granted',
        'security_storage': 'granted',
        'wait_for_update': 500
      });
    }
  }, []);

  // Load scripts based on preferences (no state changes)
  const loadScripts = useCallback((prefs) => {
    // Update Google Consent Mode v2
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': prefs.analytics ? 'granted' : 'denied',
        'ad_storage': prefs.advertising ? 'granted' : 'denied',
        'ad_user_data': prefs.advertising ? 'granted' : 'denied',
        'ad_personalization': prefs.advertising ? 'granted' : 'denied'
      });
    }

    // Load Google Analytics if analytics accepted
    if (prefs.analytics && typeof window.loadGA4 === 'function') {
      window.loadGA4();
    }

    // Load AdSense if advertising accepted
    if (prefs.advertising && typeof window.loadAdSense === 'function') {
      window.loadAdSense();
    }
  }, []);

  // Apply consent and load scripts
  const applyConsent = useCallback((prefs) => {
    // Store consent with timestamp
    const consentType = prefs.advertising && prefs.analytics ? 'all' : 
                        prefs.analytics ? 'analytics' :
                        prefs.advertising ? 'advertising' : 'necessary';
    
    localStorage.setItem('cookie_consent', consentType);
    localStorage.setItem('cookie_consent_timestamp', Date.now().toString());
    localStorage.setItem('cookie_preferences', JSON.stringify(prefs));

    loadScripts(prefs);
    setHasConsented(true);
  }, [loadScripts]);

  // Accept all cookies
  const handleAcceptAll = useCallback(() => {
    const allPrefs = { necessary: true, analytics: true, advertising: true };
    setPreferences(allPrefs);
    applyConsent(allPrefs);
    setShowBanner(false);
    setShowDetails(false);
  }, [applyConsent]);

  // Accept only necessary cookies (Reject all optional)
  const handleAcceptNecessary = useCallback(() => {
    const necessaryPrefs = { necessary: true, analytics: false, advertising: false };
    setPreferences(necessaryPrefs);
    applyConsent(necessaryPrefs);
    setShowBanner(false);
    setShowDetails(false);
  }, [applyConsent]);

  // Save custom preferences
  const handleSavePreferences = useCallback(() => {
    applyConsent(preferences);
    setShowBanner(false);
    setShowDetails(false);
  }, [preferences, applyConsent]);

  // Initialize consent mode and apply saved preferences on mount
  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    
    if (!consent) {
      // Set default consent mode to denied
      initConsentMode();
    } else if (hasConsented && preferences) {
      // Load scripts based on saved preferences (no state changes here)
      loadScripts(preferences);
    }
  }, [initConsentMode, loadScripts, hasConsented, preferences]);

  // Toggle preference
  const togglePreference = (key) => {
    if (key === 'necessary') return;
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Open settings from floating button
  const openSettings = () => {
    setShowBanner(true);
    setShowDetails(true);
  };

  return (
    <>
      {/* Floating Cookie Settings Button - Shows after consent */}
      {hasConsented && !showBanner && (
        <button
          onClick={openSettings}
          className="fixed bottom-6 left-6 z-[60] w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center focus:outline-none focus:ring-4 focus:ring-primary/50"
          aria-label={t.manageSettings}
          title={t.manageSettings}
        >
          <Cookie className="w-7 h-7" />
        </button>
      )}

      {/* Main Cookie Banner */}
      {showBanner && (
        <>
          {/* Overlay - starts below header to allow menu access */}
          <div 
            className="fixed inset-x-0 top-16 bottom-0 bg-black/50 z-[55]"
            aria-hidden="true"
          />

          {/* Cookie Banner */}
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-banner-title"
            aria-describedby="cookie-banner-desc"
            className="fixed bottom-0 left-0 right-0 z-[56] bg-background border-t shadow-2xl animate-in slide-in-from-bottom duration-300"
          >
            <div className="container mx-auto max-w-6xl px-4 py-6">
              {!showDetails ? (
                // Simple banner view
                <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Cookie className="w-6 h-6 text-primary" aria-hidden="true" />
                    </div>
                    <div className="flex-1">
                      <h2 id="cookie-banner-title" className="font-semibold text-lg mb-1">
                        {t.title}
                      </h2>
                      <p id="cookie-banner-desc" className="text-sm text-muted-foreground mb-2">
                        {t.description}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t.learnMore}{' '}
                        <Link 
                          to={`${langPrefix}/privacy`}
                          className="text-primary inline-flex items-center gap-1"
                        >
                          {t.privacyLink}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                        {' '}&{' '}
                        <Link 
                          to={`${langPrefix}/cookies`}
                          className="text-primary inline-flex items-center gap-1"
                        >
                          {t.cookiePolicyLink}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 w-full lg:w-auto">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowDetails(true)}
                      className="flex-1 lg:flex-none"
                    >
                      <Settings className="w-4 h-4 mr-1" aria-hidden="true" />
                      {t.customize}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleAcceptNecessary}
                      className="flex-1 lg:flex-none"
                    >
                      {t.acceptNecessary}
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleAcceptAll}
                      className="flex-1 lg:flex-none bg-primary"
                    >
                      <Check className="w-4 h-4 mr-1" aria-hidden="true" />
                      {t.acceptAll}
                    </Button>
                  </div>
                </div>
              ) : (
                // Detailed preferences view
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className="w-6 h-6 text-primary" aria-hidden="true" />
                      <h2 className="font-semibold text-lg">{t.title}</h2>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDetails(false)}
                      aria-label={t.close}
                    >
                      {t.close}
                    </Button>
                  </div>

                  <p className="text-sm text-muted-foreground">{t.description}</p>

                  {/* Cookie categories */}
                  <div className="space-y-3 max-h-[40vh] overflow-y-auto">
                    {/* Necessary - Always enabled */}
                    <div className="flex items-start justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex-1 pr-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-green-800 dark:text-green-200">{t.necessary}</span>
                          <span className="text-xs bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 px-2 py-0.5 rounded font-medium">
                            Always Active
                          </span>
                        </div>
                        <p className="text-xs text-green-700 dark:text-green-300">{t.necessaryDesc}</p>
                      </div>
                      <div className="relative w-11 h-6 rounded-full bg-green-500 cursor-not-allowed opacity-60">
                        <span className="absolute top-0.5 right-0.5 w-5 h-5 bg-white rounded-full shadow" />
                      </div>
                    </div>

                    {/* Analytics */}
                    <div className="flex items-start justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex-1 pr-4">
                        <span className="font-medium text-blue-800 dark:text-blue-200">{t.analytics}</span>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">{t.analyticsDesc}</p>
                      </div>
                      <button
                        role="switch"
                        aria-checked={preferences.analytics}
                        onClick={() => togglePreference('analytics')}
                        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                          preferences.analytics ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                        aria-label={t.analytics}
                      >
                        <span
                          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                            preferences.analytics ? 'right-0.5' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Advertising */}
                    <div className="flex items-start justify-between p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                      <div className="flex-1 pr-4">
                        <span className="font-medium text-orange-800 dark:text-orange-200">{t.advertising}</span>
                        <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">{t.advertisingDesc}</p>
                      </div>
                      <button
                        role="switch"
                        aria-checked={preferences.advertising}
                        onClick={() => togglePreference('advertising')}
                        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                          preferences.advertising ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                        aria-label={t.advertising}
                      >
                        <span
                          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                            preferences.advertising ? 'right-0.5' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Links to policies */}
                  <div className="text-xs text-muted-foreground pt-2 border-t">
                    {t.learnMore}{' '}
                <Link to={`${langPrefix}/privacy`} className="text-primary">{t.privacyLink}</Link>
                    {' '}&{' '}
                <Link to={`${langPrefix}/cookies`} className="text-primary">{t.cookiePolicyLink}</Link>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button 
                      variant="outline" 
                      onClick={handleAcceptNecessary}
                      className="flex-1 sm:flex-none"
                    >
                      {t.acceptNecessary}
                    </Button>
                    <Button 
                      variant="secondary"
                      onClick={handleSavePreferences}
                      className="flex-1 sm:flex-none"
                    >
                      {t.savePreferences}
                    </Button>
                    <Button 
                      onClick={handleAcceptAll}
                      className="flex-1 sm:flex-none"
                    >
                      <Check className="w-4 h-4 mr-1" aria-hidden="true" />
                      {t.acceptAll}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CookieBanner;
